/**
 * 承認一覧の絞り込みと、選んだ行の案件ごとの畳み込み。
 *
 * 画面（`ApprovalsPage`）から切り出しているのは、**絞り込みと畳み込みが描画と独立に検証
 * できる純粋な計算**だからである。行の一覧・絞り込み条件・選んだ行の集合だけを受け取り、
 * 描画の状態を持たない。
 *
 * 承認の書込（`approveTermChanges`）は案件単位で、1 回に積める行数に上限がある
 * （`APPROVAL_BATCH_LIMIT`）。一覧で選んだ行は案件を跨ぐので、送る前に案件ごとへ
 * 畳み、どの案件も上限を超えていないことを確かめる。上限を超える選択をそのまま送ると、
 * 契約の入力検証で弾かれて 1 行も積まれない。
 *
 * **行の鍵は「案件 : ノード」である**（決定 7）。承認が指すのは変更行の id では
 * なくノードの id で、同じノードを複数の案件が触りうる。ノードの id だけを鍵にすると、
 * 別の案件の同じノードの行が一緒に選ばれる。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認一覧の行を 4 軸で絞り込み、選んだ行を案件ごとに畳んで一括承認の入力にする。
 * @graph-connects none
 */
import {
  APPROVAL_BATCH_LIMIT,
  type ApprovalKind,
  type ApprovalOperation,
  type ApprovalRow,
  type ApprovalState,
} from '@cortex/domain-spec-console';

/**
 * 絞り込みの 4 軸。空文字は「絞らない」である。
 *
 * 案件は行に現れる案件番号のどれか、種別・状態・操作は契約の allowlist の値を取る。URL には
 * 出さない（`PjsPage` の絞り込みと同じ扱い）— この画面は担当者が判断のために開くもので、
 * 絞り込んだ状態を他人へ渡す対象ではない。
 *
 * 案件だけ文字列で持つのは、`<select>` の値が文字列だからである。行の案件番号は number
 * （決定 2）なので、突き合わせるときに文字列へ揃える。
 * @graph-connects none
 */
export interface ApprovalFilter {
  readonly pj: string;
  readonly kind: ApprovalKind | '';
  readonly state: ApprovalState | '';
  readonly operation: ApprovalOperation | '';
}

/**
 * 開いた直後の絞り込み。**未承認だけを出す。** この画面を開く理由は「まだ判断していない
 * 提案が残っていないか」であり、判断済みの行は済んだ仕事だからである。判断済みを読み返す
 * ときは状態の絞り込みを外す。
 * @graph-connects none
 */
export const DEFAULT_APPROVAL_FILTER: ApprovalFilter = {
  pj: '',
  kind: '',
  state: 'pending',
  operation: '',
};

/**
 * 行を一意に指す鍵。**案件とノードの組である**（同じノードを複数の案件が触りうる）。
 *
 * @param item 承認の一覧の 1 行
 * @returns 「案件 : ノード」の鍵
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認の一覧の行を案件とノードの組で一意に指す鍵を作る。
 * @graph-connects none
 */
export function approvalRowKey(item: ApprovalRow): string {
  return `${String(item.pjNumber)}:${String(item.nodeId)}`;
}

/**
 * 行の一覧を絞り込みで絞る。4 軸は AND で効く。
 *
 * @param items 一覧の行
 * @param filter 絞り込み
 * @returns 絞り込みに一致する行（元の順序を保つ）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認一覧の行を案件・種別・状態・操作の 4 軸で絞り込む。
 * @graph-connects none
 */
export function filterApprovalItems(
  items: readonly ApprovalRow[],
  filter: ApprovalFilter,
): readonly ApprovalRow[] {
  return items.filter(
    (item) =>
      (filter.pj === '' || String(item.pjNumber) === filter.pj) &&
      (filter.kind === '' || item.kind === filter.kind) &&
      (filter.state === '' || item.state === filter.state) &&
      (filter.operation === '' || item.operation === filter.operation),
  );
}

/**
 * 行に現れる案件番号を、現れた順に重複なく並べる。案件の絞り込みの選択肢になる。
 *
 * 選択肢を案件の一覧（`listPjs`）から作らないのは、承認事項の無い案件を選べても
 * 0 件になるだけだからである。行から作れば、選べる案件は必ず 1 件以上の行を持つ。
 *
 * @param items 一覧の行
 * @returns 案件番号（現れた順・重複なし）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 承認一覧の行から案件の絞り込みの選択肢を作る。
 * @graph-connects none
 */
export function pjOptionsOf(items: readonly ApprovalRow[]): readonly string[] {
  return [...new Set(items.map((item) => String(item.pjNumber)))];
}

/** 承認の書込 1 回ぶん（案件 1 件とそのノード）。 @graph-connects none */
export interface ApprovalBatch {
  readonly pjNumber: number;
  readonly nodeIds: readonly number[];
}

/**
 * 選んだ行を案件ごとに畳む。承認の書込は案件単位なので、案件の数だけ呼ぶ。
 *
 * 選ばれていない行と、一覧に無い鍵（絞り込みで隠れた行を選んだままにした場合）は落とす。
 * 送るのは**いま見えている行のうち選んだもの**だけである — 隠れた行が送られると、担当者は
 * 自分が何を承認したか画面で確かめられない。
 *
 * @param items いま見えている行
 * @param selected 選んだ行の鍵（`approvalRowKey`）
 * @returns 案件ごとの書込（案件は行に現れた順）
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 選んだ行を案件ごとに畳んで、案件単位の承認の書込に渡せる形にする。
 * @graph-connects approvalRowKey [calls] 行を一意に指す鍵
 */
export function groupSelectionByPj(
  items: readonly ApprovalRow[],
  selected: ReadonlySet<string>,
): readonly ApprovalBatch[] {
  const byPj = new Map<number, number[]>();
  for (const item of items) {
    if (!selected.has(approvalRowKey(item))) continue;
    const ids = byPj.get(item.pjNumber) ?? [];
    ids.push(item.nodeId);
    byPj.set(item.pjNumber, ids);
  }
  return [...byPj.entries()].map(([pjNumber, nodeIds]) => ({ pjNumber, nodeIds }));
}

/**
 * どれかの案件の書込が 1 回の上限を超えているか。
 *
 * @param batches 案件ごとの書込
 * @returns 超えていれば true
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 案件ごとの承認の書込が 1 回の上限（200 件）を超えていないかを判定する。
 * @graph-connects none
 */
export function exceedsBatchLimit(batches: readonly ApprovalBatch[]): boolean {
  return batches.some((batch) => batch.nodeIds.length > APPROVAL_BATCH_LIMIT);
}
